import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  Typography,
} from "@joonapp/web-shared"
import { Document, Packer, Paragraph, TextRun } from "docx"
import { saveAs } from "file-saver"
import { useState } from "react"
import toast from "react-hot-toast"

import MarkdownResultCard from "./MarkdownResultCard"
import {
  copyText,
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
  trackResultCopied,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import { generateProgressNoteTemplate } from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "progress_note_template_creator"

const ProgressNoteTemplateCreator = requireAuth(() => {
  const [description, setDescription] = useState("")
  const [goal, setGoal] = useState("")
  const [template, setTemplate] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, { has_goal: !!goal })
    try {
      const data = await generateProgressNoteTemplate(description, goal)
      setTemplate(data.template)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate template. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  const exportToDocx = async () => {
    const lines = template.split("\n")
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: lines.map(
            (line) =>
              new Paragraph({
                children: [new TextRun(line)],
              })
          ),
        },
      ],
    })
    const blob = await Packer.toBlob(doc)
    saveAs(blob, "progress_note_template.docx")
  }

  const onCopy = async () => {
    await copyText(template)
    toast.success("Copied to clipboard")
    trackResultCopied(TOOL_ID)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="Progress Note Template Creator"
      description="Describe your use case. We'll generate a progress note template you can copy or export to .docx."
    >
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Use case
        </Typography>
        <TextArea
          name="Use case"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Daily session notes for ABA therapy, focusing on behavior tracking and intervention effectiveness"
          fullWidth
        />
      </FlexBox>
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Specific goal (optional)
        </Typography>
        <TextArea
          name="Specific goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="e.g., Increase verbal communication skills"
          fullWidth
        />
      </FlexBox>
      <Button
        text="Generate template"
        onClick={generate}
        disabled={loading || !description}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {template && (
        <MarkdownResultCard
          title="Generated progress note template"
          markdown={template}
          onCopy={onCopy}
          extraActions={
            <Button
              text="Export to .docx"
              buttonType="secondary"
              onClick={exportToDocx}
            />
          }
        />
      )}
    </ToolPage>
  )
})

export default ProgressNoteTemplateCreator
